/**
 * EFFET : joue la tapette sur un cadre (le mien ou celui de mon ami) — main gantée
 * ouverte dessinée par-dessus, cadre entier qui tremble, « PAF ! » façon bande dessinée.
 */
import slapUrl from '../assets/glove-slap.svg';
import { SLAP_MS, slapFrame } from './slap';

const HAND_WIDTH = 0.5; // fraction de la largeur du cadre
const HAND_ASPECT = 1.1;

export interface SlapLayer {
  readonly element: HTMLCanvasElement;
  play(): void;
  stop(): void;
}

export function createSlapLayer(frame: HTMLElement, side: 'local' | 'remote'): SlapLayer {
  const canvas = document.createElement('canvas');
  canvas.className = 'slap';
  canvas.dataset['testid'] = 'slap';
  canvas.dataset['side'] = side;
  canvas.dataset['state'] = 'idle';
  canvas.dataset['count'] = '0';
  canvas.setAttribute('aria-hidden', 'true');
  const ctx = canvas.getContext('2d');
  const glove = new Image();
  glove.src = slapUrl;
  let raf = 0;
  let start = 0;

  const reset = (): void => {
    cancelAnimationFrame(raf);
    frame.style.transform = '';
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
  };

  const draw = (now: number): void => {
    const f = slapFrame(now - start);
    if (f.done) {
      reset();
      canvas.dataset['state'] = 'done';
      return;
    }
    raf = requestAnimationFrame(draw);
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    frame.style.transform = `translateX(${(f.shake.dx * w).toFixed(1)}px) rotate(${f.shake.rotate.toFixed(2)}deg)`;
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (f.hand && glove.complete) {
      const hw = w * HAND_WIDTH;
      const hh = hw * HAND_ASPECT;
      ctx.save();
      ctx.translate(f.hand.x * w, f.hand.y * h);
      ctx.rotate(f.hand.angle);
      ctx.drawImage(glove, -hw / 2, -hh / 2, hw, hh);
      ctx.restore();
    }
    if (f.paf > 0) drawPaf(ctx, w * 0.3, h * 0.28, Math.min(w, h) * 0.2, f.paf);
  };

  return {
    element: canvas,
    play() {
      reset();
      start = performance.now();
      canvas.dataset['state'] = 'playing';
      canvas.dataset['count'] = String(Number(canvas.dataset['count'] ?? 0) + 1);
      raf = requestAnimationFrame(draw);
      // Filet de sécurité si l'onglet est en arrière-plan (pas de requestAnimationFrame).
      setTimeout(() => {
        if (canvas.dataset['state'] === 'playing') draw(start + SLAP_MS);
      }, SLAP_MS + 200);
    },
    stop() {
      reset();
      canvas.dataset['state'] = 'idle';
    },
  };
}

/** Étoile jaune à pointes et « PAF ! » en lettres noires (cartoon années 30). */
function drawPaf(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  alpha: number,
): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(-0.15);
  ctx.beginPath();
  const spikes = 12;
  for (let i = 0; i < spikes * 2; i++) {
    const rr = i % 2 === 0 ? r : r * 0.62;
    const a = (i * Math.PI) / spikes;
    ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fillStyle = '#f2c230';
  ctx.strokeStyle = '#1c1712';
  ctx.lineWidth = Math.max(3, r * 0.06);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#1c1712';
  ctx.font = `900 ${Math.round(r * 0.55)}px 'Luckiest Guy', 'Arial Black', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('PAF !', 0, r * 0.04);
  ctx.restore();
}
